import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { InsightsFiltersOptionsQuery } from 'generated/resources/graphql'
import { isNil } from 'lodash'

import ClearFilters from 'modules/core/components/Filter/ClearFilters'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import companies from 'modules/core/store/collections/companies'

import { InsightsFilters } from 'modules/insights/hooks/useInsightsFilters'

import FilterSelector from './_FilterSelector'
import { useGenderOptions } from './hooks/useGenderOptions'
import { useLegalEntityOptions } from './hooks/useLegalEntityOptions'
import { useManagerOptions } from './hooks/useManagerOptions'
import { useNationalityOptions } from './hooks/useNationalityOptions'
import { useTeamOptions } from './hooks/useTeamOptions'
import { useWorkplaceOptions } from './hooks/useWorkplaceOptions'

type Props = {
  filterOptionsData: InsightsFiltersOptionsQuery
  filters: InsightsFilters
  isLoading: boolean
  setFilter: <T extends keyof InsightsFilters>(filterType: T, filter: InsightsFilters[T]) => void
  clearFilters: () => void
}

const Filters = ({ filterOptionsData, filters, isLoading, setFilter, clearFilters }: Props) => {
  const genderOptions = useGenderOptions(filterOptionsData)
  const legalEntityOptions = useLegalEntityOptions(filterOptionsData)
  const workplaceOptions = useWorkplaceOptions(filterOptionsData)
  const managerOptions = useManagerOptions(filterOptionsData)
  const teamOptions = useTeamOptions(filterOptionsData)
  const nationalityOptions = useNationalityOptions(filterOptionsData)

  const {
    access: { id: accessId },
  } = useCurrentSession()
  const handleClearFilters = useUnsafeCallback(() => {
    clearFilters()

    track(trackingEvents.insights.filtersCleared, {
      company_id: companies.current.attributes.id,
      access_id: accessId,
    })
  }, [clearFilters])

  const anyFilterSelected =
    Object.values(filters)
      .filter((filter) => !isNil(filter))
      .flat().length > 0

  return (
    <Box flexDirection="row" gap="s8" alignItems="center">
      <FilterSelector
        filterType="genders"
        options={genderOptions}
        filter={filters.genders}
        withEmptyOption
        setFilter={setFilter}
      />
      <FilterSelector
        filterType="legalEntities"
        options={legalEntityOptions}
        filter={filters.legalEntities}
        setFilter={setFilter}
      />
      <FilterSelector
        filterType="workplaces"
        options={workplaceOptions}
        filter={filters.workplaces}
        setFilter={setFilter}
      />
      <FilterSelector
        filterType="managers"
        options={managerOptions}
        filter={filters.managers}
        withEmptyOption
        setFilter={setFilter}
      />
      <FilterSelector
        filterType="teams"
        options={teamOptions}
        filter={filters.teams}
        setFilter={setFilter}
      />
      <FilterSelector
        filterType="nationalities"
        options={nationalityOptions}
        filter={filters.nationalities}
        withEmptyOption
        setFilter={setFilter}
      />

      {anyFilterSelected && <ClearFilters onClick={handleClearFilters} />}

      {isLoading && <Spinner size="s24" />}
    </Box>
  )
}

export default Filters
